import React, { useEffect, useMemo, useState } from "react";
import {
  NavLink,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import Home from "../../Pages/Home/Home";
import Vehicles from "../../Pages/Vehicles/Vehicles";
import AddVehicle from "../../Pages/Vehicles/AddVehicle";
import ModifyVehicle from "../../Pages/Vehicles/ModifyVehicle";
import UpdateVehicle from "../../Pages/Vehicles/UpdateVehicle";
import Manufacturers from "../../Pages/Manufacturers/Maufacturers";
import { Avatar, Button, IconButton, Tooltip, Typography } from "@mui/material";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { IoCarSportSharp } from "react-icons/io5";
import { GoDashboard } from "react-icons/go";
import { MdPrecisionManufacturing } from "react-icons/md";
import RunningText from "../../Pages/RunningText/RunningText";
import ForceUpdate from "../../Pages/ForceUpdate/ForceUpdate";
import Subscription from "../../Pages/Subscription/Subscription";
import UserSubscriptionRecords from "../../Pages/Subscription/UserSubscriptionRecords";
import Users from "../../Pages/Users/Users";
import Blogs from "../../Pages/Blogs/Blogs";
import BlogEditor from "../../Pages/Blogs/BlogEditor";
import {
  MdSubscriptions,
  MdOutlineTextFields,
  MdReceipt,
  MdSystemUpdateAlt,
  MdArticle,
  MdPeople,
} from "react-icons/md";

const SIDEBAR_COLLAPSED_KEY = "pm_admin_sidebar_collapsed";

const MENU_ITEMS = [
  { label: "Dashboard", path: "/", icon: <GoDashboard /> },
  { label: "Users", path: "/users", icon: <MdPeople /> },
  { label: "Vehicles", path: "/vehicles", icon: <IoCarSportSharp /> },
  {
    label: "Manufacturers",
    path: "/manufacturers",
    icon: <MdPrecisionManufacturing />,
  },
  { label: "Blogs", path: "/blogs", icon: <MdArticle /> },
  { label: "Running Text", path: "/running-text", icon: <MdOutlineTextFields /> },
  { label: "Force Update", path: "/force-update", icon: <MdSystemUpdateAlt /> },
  { label: "Subscription Plans", path: "/subscriptions", icon: <MdSubscriptions /> },
  {
    label: "Subscription Records",
    path: "/subscription-records",
    icon: <MdReceipt />,
  },
];

const NESTED_PAGE_META = [
  { prefix: "/Update_Vehicle/", label: "Update Vehicle", icon: <IoCarSportSharp /> },
  { path: "/add_vehicles", label: "Add Vehicle", icon: <IoCarSportSharp /> },
  { path: "/update_vehicles", label: "Modify Vehicle", icon: <IoCarSportSharp /> },
  { path: "/blogs/new", label: "New Blog Post", icon: <MdArticle /> },
  { prefix: "/blogs/", label: "Edit Blog Post", icon: <MdArticle /> },
];

function resolvePageMeta(pathname) {
  const exact = MENU_ITEMS.find((item) => item.path === pathname);
  if (exact) return exact;

  const nested = NESTED_PAGE_META.find(
    (item) =>
      item.path === pathname ||
      (item.prefix && pathname.startsWith(item.prefix))
  );
  if (nested) return nested;

  return { label: "Power Motoring", icon: <GoDashboard /> };
}

function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(
        SIDEBAR_COLLAPSED_KEY,
        sidebarCollapsed ? "true" : "false"
      );
    } catch {
      /* ignore */
    }
  }, [sidebarCollapsed]);

  const pageMeta = useMemo(
    () => resolvePageMeta(location.pathname),
    [location.pathname]
  );

  const todayLabel = useMemo(
    () =>
      new Date().toLocaleDateString("en-IN", {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
      }),
    []
  );

  const logoutAction = () => {
    sessionStorage.removeItem("wsstfaarvav");
    navigate("/Login");
    window.location.reload();
  };

  const toggleSidebar = () => setSidebarCollapsed((prev) => !prev);

  return (
    <div
      className={`app-layout ${sidebarCollapsed ? "sidebar-is-collapsed" : ""}`}
    >
      <aside className="admin-sidebar">
        <div className="sidebar-brand">
          <div className="sidebar-brand-icon" aria-hidden>
            <IoCarSportSharp />
          </div>
          <div className="sidebar-brand-copy">
            <Typography className="sidebar-brand-title">Power Motoring</Typography>
            <Typography className="sidebar-brand-subtitle">Admin console</Typography>
          </div>
          <Tooltip title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}>
            <IconButton
              className="sidebar-toggle-btn"
              size="small"
              onClick={toggleSidebar}
              aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {sidebarCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          </Tooltip>
        </div>

        <nav className="sidebar-nav" aria-label="Main navigation">
          <Typography className="sidebar-nav-label">Navigation</Typography>
          <ul className="sidebar-menu">
            {MENU_ITEMS.map((item) => {
              const link = (
                <NavLink
                  to={item.path}
                  end={
                    item.path === "/" ||
                    item.path === "/subscriptions" ||
                    item.path === "/blogs"
                  }
                  className={({ isActive }) =>
                    `sidebar-link ${isActive ? "active" : ""}`
                  }
                >
                  <span className="sidebar-link-icon">{item.icon}</span>
                  <span className="sidebar-link-label">{item.label}</span>
                </NavLink>
              );

              return (
                <li key={item.path}>
                  {sidebarCollapsed ? (
                    <Tooltip title={item.label} placement="right">
                      <span className="sidebar-link-tooltip-wrap">{link}</span>
                    </Tooltip>
                  ) : (
                    link
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <Avatar className="sidebar-user-avatar">N</Avatar>
            <div className="sidebar-user-copy">
              <Typography className="sidebar-user-name">Admin User</Typography>
              <Typography className="sidebar-user-role">Administrator</Typography>
            </div>
          </div>
          {sidebarCollapsed ? (
            <Tooltip title="Logout" placement="right">
              <IconButton
                className="sidebar-logout-icon-btn"
                onClick={logoutAction}
                aria-label="Logout"
              >
                <LogoutOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Button
              className="sidebar-logout-btn"
              variant="outlined"
              fullWidth
              startIcon={<LogoutOutlinedIcon />}
              onClick={logoutAction}
            >
              Logout
            </Button>
          )}
        </div>
      </aside>
      <div className="app-shell">
        <nav className="topbar">
          <div className="topbar-start">
            <Tooltip title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}>
              <IconButton
                className="topbar-sidebar-toggle"
                size="small"
                onClick={toggleSidebar}
                aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                {sidebarCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
              </IconButton>
            </Tooltip>
            <div className="topbar-page-icon" aria-hidden>
              {pageMeta.icon}
            </div>
            <div className="topbar-page-copy">
              <Typography className="topbar-eyebrow">Admin panel</Typography>
              <Typography className="topbar-title">{pageMeta.label}</Typography>
            </div>
          </div>

          <div className="topbar-end">
            <div className="topbar-date-chip">{todayLabel}</div>
            <div className="topbar-user-chip">
              <Avatar sx={{ bgcolor: "#2f6bff", width: 32, height: 32, fontSize: 14 }}>
                N
              </Avatar>
              <div className="topbar-user-text">
                <span className="topbar-user-name">Admin User</span>
                <span className="topbar-user-role">Administrator</span>
              </div>
            </div>
          </div>
        </nav>
        <main>
          <Routes>
            <Route path="/Login" element={<Navigate to="/" replace />} />
            <Route path="/" element={<Home />} />
            <Route path="/users" element={<Users />} />
            <Route path="/vehicles" element={<Vehicles />} />
            {/* <Route path='/Update_Vehicle/:id' element={<ModifyVehicle />} /> */}
            <Route path="/Update_Vehicle/:id" element={<UpdateVehicle />} />
            <Route path="/subscriptions" element={<Subscription />} />
            <Route
              path="/subscription-records"
              element={<UserSubscriptionRecords />}
            />
            <Route path="/add_vehicles" element={<AddVehicle />} />
            <Route path="/update_vehicles" element={<ModifyVehicle />} />
            <Route path="/manufacturers" element={<Manufacturers />} />
            <Route path="/running-text" element={<RunningText />} />
            <Route path="/force-update" element={<ForceUpdate />} />
            <Route path="/blogs" element={<Blogs />} />
            <Route path="/blogs/new" element={<BlogEditor />} />
            <Route path="/blogs/:id" element={<BlogEditor />} />
            <Route
              path="/running_text"
              element={<Navigate to="/running-text" replace />}
            />
          </Routes>
        </main>
      </div>
    </div>
  );
}

export default Layout;
